import {
  useCallback,
  useRef,
  useState,
  type PropsWithChildren,
  type RefObject,
} from "react";
import { DoubleLeftOutlined, SearchOutlined } from "@ant-design/icons";
import { Drawer } from "antd";
import classNames from "classnames";

import { useLocation } from "react-router-dom";

import { useAppSelector } from "../../../hooks/redux";
import { useHideIntercom } from "../../hooks/useHideIntercom";
import { hasProjectNeoAccess } from "../../../util/subdomain";
import NeoDrawer from "../../neo/NeoDrawer";
import {
  NeoDrawerProvider,
  useNeoDrawer,
} from "../../neo/NeoDrawer/NeoDrawerContext";
import { NeoSparkIcon } from "../../neo/NeoSparkIcon";
import {
  PatientPeekPanel,
  PatientPeekProvider,
} from "../../neo/PatientPeek/PatientPeekProvider";
import AppNavigation from "../../navigation/AppNavigation";
import QuickSearchModal from "../../navigation/AppNavigation/QuickSearchModal";
import styles from "./styles.module.scss";
import { useCollapsingPageTitle } from "./useCollapsingPageTitle";

/**
 * Visual application shell only. Authentication, clinic bootstrap, error
 * boundaries and server data remain owned by the existing MobileLayout and
 * page/controller layers.
 *
 * The "Ask Circadia AI" panel docks to the right of the page: the page keeps
 * its own scroll and narrows instead of being covered.
 *
 * ``compact`` (phones and portrait tablets): the navigation isn't a persistent
 * column. A pinned top bar holds, on the left, the navigation button (opens
 * the same navigation as an overlay drawer from the left, over the page rather
 * than compressing it) and patient search; the page title once the page's
 * large title scrolls away (iOS-style); and, on the right, "Ask Circadia AI".
 */
interface AppShellLayoutProps extends PropsWithChildren {
  onLogout: () => void;
  compact?: boolean;
}

const NAVIGATION_DRAWER_WIDTH = 282;

interface CompactTopBarProps {
  topBarRef: RefObject<HTMLElement>;
  title: string | null;
  navigationOpen: boolean;
  onOpenNavigation: () => void;
}

/** Rendered inside NeoDrawerProvider so it can open the assistant. */
const CompactTopBar = ({
  topBarRef,
  title,
  navigationOpen,
  onOpenNavigation,
}: CompactTopBarProps) => {
  const { user, betaFeaturesAllowedList } = useAppSelector((state) => ({
    user: state.auth.user,
    betaFeaturesAllowedList: state.auth.betaFeaturesAllowedList,
  }));
  const neoDrawer = useNeoDrawer();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  // Chats are "Neo" in the bar; the conversation title stays the page's own
  // header. Other pages show their large title once it scrolls away.
  const barTitle = location.pathname.startsWith("/copilot") ? "Neo" : title;
  const showNeo = hasProjectNeoAccess({
    email: user?.email,
    betaFeaturesAllowedList,
  });

  return (
    <header
      ref={topBarRef}
      className={classNames(styles.topBar, {
        [styles.topBarCollapsed]: !!barTitle,
      })}
    >
      <div className={styles.topBarActions}>
        <button
          type="button"
          className={styles.iconButton}
          aria-label="Open navigation"
          aria-expanded={navigationOpen}
          onClick={onOpenNavigation}
        >
          <DoubleLeftOutlined />
        </button>
        {/* Same patient search as the sidebar's "Search patients". */}
        <button
          type="button"
          className={styles.iconButton}
          aria-label="Search patients"
          onClick={() => setSearchOpen(true)}
        >
          <SearchOutlined />
        </button>
      </div>
      <span
        className={styles.topBarTitle}
        aria-hidden={!barTitle}
        data-testid="top-bar-title"
      >
        {barTitle}
      </span>
      <div className={classNames(styles.topBarActions, styles.topBarEnd)}>
        {showNeo && neoDrawer && (
          <button
            type="button"
            className={classNames(styles.iconButton, {
              [styles.iconButtonActive]: neoDrawer.open,
            })}
            aria-label="Ask Circadia AI"
            aria-pressed={neoDrawer.open}
            onClick={() =>
              neoDrawer.open ? neoDrawer.close() : neoDrawer.openNewChat()
            }
          >
            <NeoSparkIcon size={16} />
          </button>
        )}
      </div>
      <QuickSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </header>
  );
};

const AppShellLayout = ({
  children,
  onLogout,
  compact = false,
}: AppShellLayoutProps) => {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const closeNavigation = useCallback(() => setNavigationOpen(false), []);
  const topBarRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const title = useCollapsingPageTitle(bodyRef, topBarRef, compact);

  // Phones and portrait tablets: Intercom's floating bubble covers page
  // content and composers, so hide it (actions that open the messenger, e.g.
  // "Ask for more credits", still work; only the launcher is hidden).
  useHideIntercom(compact);

  return (
    <NeoDrawerProvider>
      <PatientPeekProvider>
        <div
          className={classNames(styles.shell, { [styles.compact]: compact })}
          data-compact-shell={compact || undefined}
        >
          {compact ? (
            <Drawer
              open={navigationOpen}
              onClose={closeNavigation}
              placement="left"
              width={NAVIGATION_DRAWER_WIDTH}
              closable={false}
              title={null}
              rootClassName={styles.navigationDrawer}
              styles={{ body: { padding: 0 } }}
            >
              <AppNavigation
                onLogout={onLogout}
                variant="drawer"
                onRequestClose={closeNavigation}
              />
            </Drawer>
          ) : (
            <AppNavigation onLogout={onLogout} />
          )}
          <main className={styles.content}>
            {compact && (
              <CompactTopBar
                topBarRef={topBarRef}
                title={title}
                navigationOpen={navigationOpen}
                onOpenNavigation={() => setNavigationOpen(true)}
              />
            )}
            <div ref={bodyRef} id="body" className={styles.body}>
              {children}
            </div>
          </main>
          <NeoDrawer />
          <PatientPeekPanel />
        </div>
      </PatientPeekProvider>
    </NeoDrawerProvider>
  );
};

export default AppShellLayout;
